import { inject, Injectable, Type } from '@angular/core';
import { EncryptedStorageService, RecentProjectsService } from '../../services';
import { OCP_ANALYTICS } from '../matomo/matomo-analytics.config';
import { STORAGE_KEYS } from '../encryption/storage.types';
import { ProjectEventBus } from '@web-portal/domains/shared/event-bus';
import { Observable, catchError, map, of, tap, throwError } from 'rxjs';
import {
  CreateProjectParams,
  GetProjectsParams,
  ProjectsAdapterService,
  TaskShortInfoCategory,
} from '../../providers';
import { BIMPLUS_API_CONFIG } from '@web-portal/domains/shared/bimplus-core';
import { TrackAction } from '../../decorators';
import { Router } from '@angular/router';
import { ROUTE } from '../../constants/route';
import {
  ProjectFacadeStrategy,
  ProjectMapperStrategy,
  ProjectsServiceStrategy,
  TeamFacadeStrategy,
} from '../../strategies';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import {
  GetTasksParams,
  Project,
  ProjectBuilder,
  Task,
} from '@web-portal/domains/shared/domain-models';
import { toast } from 'ngx-sonner';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class ProjectsService extends ProjectsServiceStrategy {
  private readonly encryptedStorage = inject(EncryptedStorageService);
  private readonly projectEventBus = inject(ProjectEventBus);
  private readonly projectsAdapter = inject(ProjectsAdapterService);
  private readonly projectFacade = inject(ProjectFacadeStrategy);
  private readonly teamFacade = inject(TeamFacadeStrategy);
  private readonly recentProjectsService = inject(RecentProjectsService);
  private readonly apiConfig = inject(BIMPLUS_API_CONFIG, { optional: true });
  private readonly router = inject(Router);
  private readonly _hlmDialogService = inject(HlmDialogService);
  private readonly projectMapper = inject(ProjectMapperStrategy);
  private readonly translateService = inject(TranslateService);

  getTasksShortInfo(projectId: string) {
    return this.projectsAdapter.getTasksShortInfo(projectId);
  }

  getProjects(params?: GetProjectsParams) {
    return this.projectsAdapter.getProjects(params) as unknown as Observable<Project[]>;
  }

  getProjectById(projectId: string, params?: string | undefined) {
    return this.projectsAdapter.getProjectById(
      projectId,
      params as Omit<GetProjectsParams, 'teamSlug' | 'alsoRecycleBin'> | undefined
    ) as unknown as Observable<Project>;
  }

  loadSelectedProject(): Project | null {
    if (!this.encryptedStorage.isInitialized()) {
      return null;
    }
    return this.encryptedStorage.getItem<Project>(STORAGE_KEYS.SELECTED_PROJECT);
  }

  saveSelectedProject(project: Project): void {
    if (!this.apiConfig) {
      return;
    }
    this.projectEventBus.emit({
      type: '[Project] selected',
      payload: this.projectMapper.mapToProject(project, this.apiConfig),
    });
    if (!this.encryptedStorage.isInitialized()) {
      return;
    }
    this.encryptedStorage.setItemAndSave(STORAGE_KEYS.SELECTED_PROJECT, project);
  }

  removeItemAndSave(): void {
    this.projectEventBus.emit({ type: '[Project] selected', payload: null });
    if (this.encryptedStorage.isInitialized()) {
      this.encryptedStorage.removeItemAndSave(STORAGE_KEYS.SELECTED_PROJECT);
    }
  }

  getTasks(projectId: string, params?: unknown) {
    return this.projectsAdapter.getTasks(
      projectId,
      params as GetTasksParams
    ) as unknown as Observable<Task[]>;
  }

  getProjectsForTeam(teamSlug = '') {
    return this.projectsAdapter.getProjects({ teamSlug: teamSlug }).pipe(
      tap({
        next: projects => {
          if (this.apiConfig) {
            this.projectEventBus.emit({
              type: '[Project] updated',
              payload: projects.map(p => this.projectMapper.mapToProject(p, this.apiConfig!)),
            });
          }
        },
      })
    ) as unknown as Observable<Project[]>;
  }

  createProject(params: CreateProjectParams) {
    return this.projectsAdapter.createProject(params).pipe(
      tap({
        next: project => {
          if (this.apiConfig) {
            this.projectEventBus.emit({
              type: '[Project] updated',
              payload: this.projectMapper.mapToProject(project, this.apiConfig),
            });
          }
        },
      })
    ) as unknown as Observable<Project>;
  }

  @TrackAction(OCP_ANALYTICS.ACTION.CREATE, OCP_ANALYTICS.TOKEN.SIDEBAR_CREATE_NEW_PROJECT)
  createProjectDialog(component: Type<unknown>, strategy: unknown): void {
    const ref = this._hlmDialogService.open(component, {
      context: {
        strategy,
        onSubmit: (name: string) => this.createProject({ name, shortDescr: '' }),
        errorMessages: {
          nameConflict:
            'create-form.project.error.a-project-with-this-name-already-exists-choose-a-different-name',
          generic: 'create-form.project.error.an-unexpected-error-occurred-please-try-again',
        },
        showCloseButton: false,
      },
    });
    ref?.closed$.subscribe({
      next: (result: unknown) => {
        if (result) {
          const r = result as { id: string; name: string };
          const team = this.teamFacade.selectedTeam();
          if (!team) {
            console.error('No team selected');
            return;
          }

          this.recentProjectsService.addRecentProject(r.id, r.name, team.id, team.name, team.slug, {
            thumbnailUrl: undefined,
            modelsCount: 0,
            tasksCount: 0,
          });

          void this.router.navigate(['/', ROUTE.teams, team.id, ROUTE.projects, r.id]);
        }
      },
    });
  }

  openRenameProjectDialog(component: Type<unknown>, strategy: unknown): void {
    const selectedProject = this.projectFacade.selectedProject();
    if (!selectedProject) {
      return;
    }

    const ref = this._hlmDialogService.open(component, {
      context: {
        strategy,
        initialValue: selectedProject.name,
        showCloseButton: false,
      },
    });

    ref?.closed$.subscribe({
      next: result => {
        const name = (result as { name?: string } | undefined)?.name?.trim();
        if (!name || name === selectedProject.name) {
          return;
        }

        this.renameProject(name).subscribe({
          error: error => {
            console.error('[ProjectsService] renameProject failed', error);
            toast.error(this.translateService.instant('projects.settings.rename-project-failed'), {
              duration: 5000,
            });
          },
        });
      },
    });
  }

  renameProject(name: string): Observable<Project> {
    const selectedProject = this.projectFacade.selectedProject();
    const normalizedName = name.trim();

    if (!selectedProject?.id) {
      return throwError(() => new Error('No selected project available for rename'));
    }

    if (!normalizedName || normalizedName === selectedProject.name) {
      return of(selectedProject);
    }

    return this.projectsAdapter.renameProject(selectedProject.id, normalizedName).pipe(
      map(() => ({ ...selectedProject, name: normalizedName })),
      tap(project => {
        this.emitProjectUpdated(project);
        this.saveSelectedProject(project);
        this.recentProjectsService.updateRecentProjectName(project.id, project.name);
      })
    );
  }

  uploadSelectedProjectThumbnail(file: File): Observable<Project> {
    const selectedProject = this.projectFacade.selectedProject();

    if (!selectedProject?.id) {
      return throwError(() => new Error('No selected project available for thumbnail upload'));
    }

    return this.projectsAdapter.uploadProjectThumbnail(selectedProject.id, file).pipe(
      map(response => ({
        ...selectedProject,
        thumbnailUrl: this.apiConfig
          ? new ProjectBuilder()
              .withThumbnailUrl(
                { id: selectedProject.id, thumbnail: response.url, modified: Date.now() },
                this.apiConfig
              )
              .build().thumbnailUrl
          : undefined,
      })),
      tap(project => {
        this.emitProjectUpdated(project);
        this.recentProjectsService.updateRecentProjectThumbnail(project.id, project.thumbnailUrl);
        toast.success(this.translateService.instant('projects.settings.thumbnail-upload-success'), {
          duration: 5000,
        });
      }),
      catchError(error => {
        console.error('[ProjectsService] uploadSelectedProjectThumbnail failed', error);
        toast.error(this.translateService.instant('projects.settings.thumbnail-upload-failed'), {
          duration: 5000,
        });
        return throwError(() => error);
      })
    );
  }

  deleteSelectedProjectThumbnail(): Observable<Project> {
    const selectedProject = this.projectFacade.selectedProject();

    if (!selectedProject?.id) {
      return throwError(() => new Error('No selected project available for thumbnail deletion'));
    }

    return this.projectsAdapter.deleteProjectThumbnail(selectedProject.id).pipe(
      map(() => ({ ...selectedProject, thumbnailUrl: undefined })),
      tap(project => {
        this.emitProjectUpdated(project);
        this.recentProjectsService.updateRecentProjectThumbnail(project.id, undefined);
        toast.success(this.translateService.instant('projects.settings.thumbnail-delete-success'), {
          duration: 5000,
        });
      }),
      catchError(error => {
        console.error('[ProjectsService] deleteSelectedProjectThumbnail failed', error);
        toast.error(this.translateService.instant('projects.settings.thumbnail-delete-failed'), {
          duration: 5000,
        });
        return throwError(() => error);
      })
    );
  }

  private emitProjectUpdated(project: Project): void {
    this.projectEventBus.emit({ type: '[Project] updated', payload: project });
  }

  static extractTotalTaskCount(categories: TaskShortInfoCategory[]): number {
    const allTask = categories.find(c => c.name === 'AllTask');
    if (!allTask) return 0;
    return allTask.countinfo.reduce((sum, info) => sum + info.issuescount, 0);
  }
}
